import {
  deleteAssistApiTemplate,
  loadAssistApiTemplates,
  loadAssistTemplates as loadLocalAssistTemplates,
  saveAssistApiTemplate,
  saveAssistTemplates,
} from "@mirage/service-compose";
import { tagged } from "@mirage/service-logging";
import { AccessType } from "@mirage/shared/compose/assist-api";
import { createDefaultTemplates } from "@mirage/shared/compose/assist-template";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import type {
  AssistCustomTemplate,
  DefaultTemplate,
} from "@mirage/shared/compose/assist-template";

const logger = tagged("AssistTemplatesContext");

// Sort templates consistently: team templates first, then individual, alphabetically by name
const sortTemplates = (
  templates: AssistCustomTemplate[],
): AssistCustomTemplate[] => {
  return [...templates].sort((a, b) => {
    if (a.accessType !== b.accessType) {
      if (a.accessType === AccessType.TEAM) return -1;
      if (b.accessType === AccessType.TEAM) return 1;
    }
    return a.name.localeCompare(b.name);
  });
};

export interface AssistTemplatesContextInterface {
  templates: AssistCustomTemplate[] | undefined;
  saveTemplate: (
    template: AssistCustomTemplate,
    onSaved?: (savedTemplate: AssistCustomTemplate) => void,
  ) => Promise<void>;
  deleteTemplate: (templateId: string) => Promise<void>;
  defaultTemplates: DefaultTemplate[];
}

export const AssistTemplatesContext =
  createContext<AssistTemplatesContextInterface | null>(null);

export const useAssistTemplatesContext = () => {
  const context = useContext(AssistTemplatesContext);
  if (!context) {
    throw new Error(
      "useAssistTemplatesContext must be used within an AssistTemplatesContextProvider",
    );
  }
  return context;
};

interface AssistTemplatesContextProviderProps {
  children: React.ReactNode;
}

export const AssistTemplatesContextProvider = ({
  children,
}: AssistTemplatesContextProviderProps) => {
  const defaultTemplates = useMemo(() => createDefaultTemplates(), []);
  const [templates, setTemplates] = useState<
    AssistCustomTemplate[] | undefined
  >(undefined);
  useEffect(() => {
    async function loadTemplates() {
      const localTemplates = await loadLocalAssistTemplates();
      if (localTemplates.length) {
        await Promise.all(localTemplates.map(saveAssistApiTemplate))
          .then(() => {
            saveAssistTemplates([]);
            return;
          })
          .catch();
      }
      const apiTemplates = await loadAssistApiTemplates();
      setTemplates(sortTemplates(apiTemplates));
    }
    loadTemplates();
  }, []);

  const saveTemplate = useCallback(
    async (
      template: AssistCustomTemplate,
      onSaved?: (savedTemplate: AssistCustomTemplate) => void,
    ) => {
      // Optimistically update the UI immediately
      setTemplates((prevTemplates) => {
        if (!prevTemplates) return sortTemplates([template]);

        const existingIndex = prevTemplates.findIndex(
          (t) => t.id === template.id,
        );
        let updated: AssistCustomTemplate[];
        if (existingIndex >= 0) {
          updated = [...prevTemplates];
          updated[existingIndex] = template;
        } else {
          updated = [...prevTemplates, template];
        }
        return sortTemplates(updated);
      });

      saveAssistApiTemplate(template)
        .then(async (savedTemplate) => {
          // re-fetch templates to ensure we have the latest server state
          const updatedTemplates = await loadAssistApiTemplates();
          setTemplates(sortTemplates(updatedTemplates));
          // re-save the new template with the dataId
          if (onSaved && savedTemplate) {
            onSaved(savedTemplate);
          }
          return;
        })
        .catch((error) => {
          logger.error("Failed to save template", error);
          // Revert the optimistic update on error
          void loadAssistApiTemplates().then((templates) =>
            setTemplates(sortTemplates(templates)),
          );
        });
    },
    [],
  );

  const deleteTemplate = useCallback((templateId: string) => {
    return new Promise<void>((resolve) => {
      setTemplates((prevTemplates) => {
        if (prevTemplates === undefined) {
          logger.warn("attempted to delete template before loading completes");
          resolve();
          return prevTemplates;
        }

        const template = prevTemplates.find((t) => t.id === templateId);
        if (template) {
          deleteAssistApiTemplate(template)
            .then(() => {
              return resolve();
            })
            .catch();
        } else {
          resolve();
        }

        const updatedTemplates = prevTemplates.filter(
          (t) => t.id !== templateId,
        );
        return updatedTemplates;
      });
    });
  }, []);

  return (
    <AssistTemplatesContext.Provider
      value={{ templates, saveTemplate, deleteTemplate, defaultTemplates }}
    >
      {children}
    </AssistTemplatesContext.Provider>
  );
};
